import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeature } from '../contexts/FeatureContext';
import { Video, PenTool, Star, Clock, Zap, MessageSquare, Edit, Save, X, Share2, QrCode, Copy, Download } from 'lucide-react';
import { db } from '../services/firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeature();
  const [greeting, setGreeting] = useState('');
  const [recentTools, setRecentTools] = useState([]);
  const [userForms, setUserForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingForms, setLoadingForms] = useState(true);
  const [editingFormId, setEditingFormId] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentShareForm, setCurrentShareForm] = useState(null);

  // Set greeting based on time of day and fetch recent tools
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
    } else if (hour < 17) {
      setGreeting('Good Afternoon');
    } else {
      setGreeting('Good Evening');
    }

    // Fetch user's forms from Firestore
    const fetchUserForms = async () => {
      if (!currentUser?.uid) return;
      
      try {
        setLoadingForms(true);
        const formsRef = collection(db, 'forms');
        const q = query(
          formsRef,
          where('userId', '==', currentUser.uid),
          orderBy('updatedAt', 'desc')
        );
        
        const formsSnapshot = await getDocs(q);
        const formsList = [];
        
        formsSnapshot.forEach((doc) => {
          formsList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setUserForms(formsList);
      } catch (error) {
        console.error('Error fetching forms:', error);
      } finally {
        setLoadingForms(false);
      }
    };
    
    fetchUserForms();

    // Fetch user's recent activity from Firestore
    const fetchRecentActivity = async () => {
      if (!currentUser?.uid) return;
      
      try {
        setLoading(true);
        const userActivityRef = collection(db, 'userActivity');
        const q = query(
          userActivityRef,
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(30)
        );
        
        const activitySnapshot = await getDocs(q);
        const recentActivities = [];
        
        // If we have more than 30 activities, delete the oldest ones
        if (activitySnapshot.size > 30) {
          const oldestActivities = activitySnapshot.docs.slice(30);
          const batch = writeBatch(db);
          oldestActivities.forEach(doc => {
            batch.delete(doc.ref);
          });
          await batch.commit();
        }
        
        activitySnapshot.forEach((doc) => {
          const data = doc.data();
          // Map tool IDs to their icon components
          let iconComponent = PenTool;
          if (data.toolId === 'youtube-reel') {
            iconComponent = Video;
          } else if (data.toolId === 'form-connect') {
            iconComponent = MessageSquare;
          }
          
          // Format the date
          const timestamp = data.timestamp?.toDate() || new Date();
          const now = new Date();
          const diffTime = Math.abs(now - timestamp);
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
          
          let dateText = '';
          if (diffDays > 0) {
            dateText = `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
          } else if (diffHours > 0) {
            dateText = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
          } else {
            dateText = 'Just now';
          }
          
          recentActivities.push({
            id: data.toolId,
            name: data.toolName || 'Unknown Tool',
            icon: iconComponent,
            date: dateText
          });
        });
        
        setRecentTools(recentActivities);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentActivity();
  }, [currentUser]);

  // Function to handle editing a form's WhatsApp number
  const handleEditWhatsapp = (formId, currentNumber) => {
    setEditingFormId(formId);
    setWhatsappNumber(currentNumber || '');
  };

  // Function to save the updated WhatsApp number
  const handleSaveWhatsapp = async (formId) => {
    try {
      const formRef = doc(db, 'forms', formId);
      
      // Find the form in our state
      const form = userForms.find(f => f.id === formId);
      if (!form) return;
      
      // Update the form settings with the new WhatsApp number
      const updatedSettings = {
        ...form.settings,
        whatsappNumber: whatsappNumber
      };
      
      // Update Firestore
      await updateDoc(formRef, {
        'settings.whatsappNumber': whatsappNumber
      });
      
      // Update local state
      setUserForms(userForms.map(form => 
        form.id === formId 
          ? { ...form, settings: { ...form.settings, whatsappNumber: whatsappNumber } } 
          : form
      ));
      
      // Reset editing state
      setEditingFormId(null);
      setWhatsappNumber('');
    } catch (error) {
      console.error('Error updating WhatsApp number:', error);
      alert('Failed to update WhatsApp number. Please try again.');
    }
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingFormId(null);
    setWhatsappNumber('');
  };

  // Function to handle sharing a form
  const handleShareForm = (form) => {
    if (!form.settings?.whatsappNumber) {
      // Show a more descriptive error message about WhatsApp number importance
      alert('You need to add your WhatsApp number before sharing this form. Your WhatsApp number is important to receive form submissions directly to your phone.');
      handleEditWhatsapp(form.id, '');
      return;
    }
    
    setCurrentShareForm(form);
    setShowShareModal(true);
  };

  // Function to copy form URL to clipboard
  const copyFormUrl = (formId) => {
    const url = `${window.location.origin}/form/${formId}`;
    navigator.clipboard.writeText(url);
    alert('Form URL copied to clipboard!');
  };

  // Generate form URL
  const getFormUrl = (formId) => {
    return `${window.location.origin}/form/${formId}`;
  };

  // Array of available tools
  const availableTools = [
    {
      id: 'youtube-reel',
      name: 'YouTube Reel Creator',
      description: 'Extract segments from YouTube videos for social media',
      icon: Video,
      popular: true,
    },
    {
      id: 'form-connect',
      name: 'FormConnect',
      description: 'Create custom forms with WhatsApp integration',
      icon: MessageSquare,
      popular: true,
    },
    {
      id: 'social-media-content',
      name: 'Social Media Content Generator',
      description: 'Create engaging, platform-optimized social media content using AI',
      icon: Star,
      popular: true,
    },
    {
      id: 'digital-identity',
      name: 'Digital Identity Hub',
      description: 'Create and share digital business cards with QR codes',
      icon: QrCode,
      popular: true,
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 p-6 rounded-xl shadow-sm border border-indigo-100">
        <h1 className="text-3xl font-bold">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
            {greeting}, {currentUser?.displayName || 'there'}!
          </span>
        </h1>
        <p className="text-gray-600 mt-2 max-w-3xl">
          Welcome to your KangleiCode Tools dashboard. Here's everything you need to get started.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          {isFeatureEnabled('youtube-reel') && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              <Video className="h-3 w-3 mr-1" />YouTube Reel Creator
            </span>
          )}
          {isFeatureEnabled('form-connect') && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <MessageSquare className="h-3 w-3 mr-1" />FormConnect
            </span>
          )}
          {isFeatureEnabled('social-media-content') && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              <Star className="h-3 w-3 mr-1" />Social Media Content
            </span>
          )}
          {isFeatureEnabled('digital-identity') && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <QrCode className="h-3 w-3 mr-1" />Digital Identity Hub
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 mb-8 shadow-sm border border-indigo-100">
        <h2 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center">
          <Zap className="mr-2 h-5 w-5 text-indigo-500" />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">Quick Actions</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* YouTube Reel Creator - only show if feature is enabled */}
          {isFeatureEnabled('youtube-reel') && (
            <Link
              to="/youtube-reel"
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center transform hover:-translate-y-1 border border-gray-100 hover:border-indigo-200"
            >
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <Video className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Create Reel</h3>
                <p className="text-sm text-gray-500">YouTube to social media</p>
              </div>
            </Link>
          )}
          
          {/* FormConnect - only show if feature is enabled */}
          {isFeatureEnabled('form-connect') && (
            <Link
              to="/form-connect"
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center"
            >
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <MessageSquare className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Create Form</h3>
                <p className="text-sm text-gray-500">WhatsApp integrated forms</p>
              </div>
            </Link>
          )}
          
          {/* Social Media Content Generator - only show if feature is enabled */}
          {isFeatureEnabled('social-media-content') && (
            <Link
              to="/social-media-content"
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center"
            >
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <Star className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Generate Content</h3>
                <p className="text-sm text-gray-500">AI social media content</p>
              </div>
            </Link>
          )}
          
          {/* Digital Identity Hub - only show if feature is enabled */}
          {isFeatureEnabled('digital-identity') && (
            <Link
              to="/digital-identity"
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center"
            >
              <div className="bg-indigo-100 p-3 rounded-full mr-4">
                <QrCode className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Digital Identity</h3>
                <p className="text-sm text-gray-500">Business cards with QR</p>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Your Forms Section with WhatsApp Settings */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <MessageSquare className="mr-2 h-5 w-5 text-indigo-500" />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">Your Forms</span>
        </h2>
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          {loadingForms ? (
            <div className="p-6 text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <p>Loading your forms...</p>
            </div>
          ) : userForms.length > 0 ? (
            <div>
              <div className="px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Form WhatsApp Settings
              </div>
              <ul className="divide-y divide-gray-200">
                {userForms.map((form) => (
                  <li key={form.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{form.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {form.fields?.length || 0} fields • {form.submissions || 0} submissions
                        </p>
                      </div>
                      
                      {editingFormId === form.id ? (
                        <div className="flex items-center space-x-2">
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">+</span>
                            </div>
                            <input
                            type="tel"
                            value={whatsappNumber}
                            onChange={(e) => setWhatsappNumber(e.target.value)}
                            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                            placeholder="WhatsApp Number"
                              autoFocus
                          />
                          </div>
                          <button
                            onClick={() => handleSaveWhatsapp(form.id)}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <div className="text-sm text-gray-500 mr-3">
                            <span className="font-medium">WhatsApp:</span> {form.settings?.whatsappNumber || 'Not set'}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditWhatsapp(form.id, form.settings?.whatsappNumber)}
                              className="inline-flex items-center p-1.5 border border-gray-300 rounded-full text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              title="Edit WhatsApp Number"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleShareForm(form)}
                              className="inline-flex items-center p-1.5 border border-gray-300 rounded-full text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              title="Share Form"
                            >
                              <Share2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => window.open(`/form/${form.id}/preview`, '_blank')}
                              className="inline-flex items-center p-1.5 border border-gray-300 rounded-full text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                              title="Preview Form"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p>You haven't created any forms yet. <Link to="/form-connect" className="text-indigo-600 hover:text-indigo-500">Create your first form</Link>.</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="mr-2 h-5 w-5 text-indigo-500" />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">Recent Activity</span>
        </h2>
        <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
          {loading ? (
            <div className="p-6 text-center text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-4"></div>
              <p>Loading your recent activity...</p>
            </div>
          ) : recentTools.length > 0 ? (
            <div className="relative">
              <ul className="divide-y divide-gray-200 max-h-[300px] overflow-y-auto">
                {recentTools.slice(0, 3).map((tool, index) => (
                  <li key={`${tool.id}-${index}`} className="p-4 hover:bg-gray-50 transition-colors duration-200 border-l-2 border-transparent hover:border-indigo-500">
                    <Link to={`/${tool.id}`} className="flex items-center">
                      <div className="bg-gradient-to-br from-indigo-100 to-blue-100 p-3 rounded-full mr-4 shadow-sm">
                        <tool.icon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{tool.name}</p>
                        <p className="text-sm text-gray-500">{tool.date}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              {recentTools.length > 3 && (
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p>No recent activity found. Start using tools to see your history here.</p>
            </div>
          )}
        </div>
      </div>

      {/* All Tools Section */}
      {/* All Tools Section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <PenTool className="mr-2 h-5 w-5 text-indigo-500" />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">All Tools</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {availableTools.filter(tool => isFeatureEnabled(tool.id)).map((tool) => (
            <Link
              key={tool.id}
              to={`/${tool.id}`}
              className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col transform hover:-translate-y-1 border border-gray-100 hover:border-indigo-200"
            >
              <div className="px-6 py-5">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-indigo-100 to-blue-100 p-3 rounded-full mr-4 shadow-inner">
                    <tool.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{tool.name}</h3>
                    {tool.popular && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 mt-1 border border-amber-200 shadow-sm">
                        <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                        Popular
                      </span>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-gray-600 text-sm">{tool.description}</p>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-indigo-50 px-6 py-3 text-right">
                <span className="inline-flex items-center text-xs font-medium text-indigo-600 group-hover:text-indigo-800">
                  Use Tool
                  <svg className="h-4 w-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && currentShareForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex justify-between items-center">
              <h3 className="text-lg font-medium">Share Form</h3>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setCurrentShareForm(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Form URL</h4>
                <div className="flex items-center">
                  <input
                    type="text"
                    readOnly
                    value={getFormUrl(currentShareForm.id)}
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => copyFormUrl(currentShareForm.id)}
                    className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-3 py-2 rounded-r-md hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">QR Code</h4>
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg border border-gray-200 mb-3">
                    <QRCodeSVG
                      value={getFormUrl(currentShareForm.id)}
                      size={200}
                      bgColor={"#ffffff"}
                      fgColor={"#000000"}
                      level={"L"}
                      includeMargin={false}
                    />
                  </div>
                  <button
                    onClick={() => {
                      // Create a download link for the QR code
                      const svg = document.querySelector('.bg-white.p-4.rounded-lg.border svg');
                      if (svg) {
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          canvas.width = img.width;
                          canvas.height = img.height;
                          ctx.drawImage(img, 0, 0);
                          const pngFile = canvas.toDataURL('image/png');
                          const downloadLink = document.createElement('a');
                          downloadLink.download = `${currentShareForm.name.replace(/\s+/g, '-').toLowerCase()}-qrcode.png`;
                          downloadLink.href = pngFile;
                          downloadLink.click();
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-gradient-to-r from-indigo-100 to-blue-100 hover:from-indigo-200 hover:to-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 shadow-sm"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download QR Code
                  </button>
                </div>
              </div>
              
              <div className="mt-6 text-center text-xs">
                <p className="text-gray-500 mb-1">Make sure your WhatsApp number is set correctly before sharing this form.</p>
                <div className="bg-indigo-50 rounded-md py-2 px-3 inline-flex items-center">
                  <MessageSquare className="h-4 w-4 text-indigo-500 mr-2" />
                  <p className="text-indigo-700">Current WhatsApp number: <span className="font-medium">{currentShareForm.settings?.whatsappNumber}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
